import React from 'react';
import { ExternalLink, Facebook, Instagram, Youtube, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

const Resources = () => {
  const socialLinks = [
    {
      name: "Google Business",
      url: "https://www.google.com/maps/search/Maulana+Zubair+divine+school+of+motoring+Coventry",
      description: "Read our reviews and see photos of our vehicles and lessons",
      icon: <ExternalLink className="w-6 h-6" />,
      color: "bg-blue-600 hover:bg-blue-700"
    },
    {
      name: "TikTok",
      url: "https://www.tiktok.com/@divineschoolofmotoringmaulanazubair",
      description: "Watch our driving tips and lesson videos",
      icon: <ExternalLink className="w-6 h-6" />,
      color: "bg-slate-900 hover:bg-slate-800"
    },
    {
      name: "YouTube",
      url: "https://www.youtube.com/@MaulanaZubairDivineSchoolOfMotoring",
      description: "Subscribe for driving tutorials and success stories",
      icon: <Youtube className="w-6 h-6" />,
      color: "bg-red-600 hover:bg-red-700"
    },
    {
      name: "Facebook",
      url: "https://www.facebook.com/divineschoolofmotoring",
      description: "Connect with us on Facebook",
      icon: <Facebook className="w-6 h-6" />,
      color: "bg-blue-700 hover:bg-blue-800"
    },
    {
      name: "Instagram",
      url: "https://www.instagram.com/dsmcoventry",
      description: "Follow our Instagram for updates and photos",
      icon: <Instagram className="w-6 h-6" />,
      color: "bg-pink-600 hover:bg-pink-700"
    }
  ];

  const usefulLinks = [
    {
      name: "DVSA - Driver and Vehicle Standards Agency",
      url: "https://www.gov.uk/government/organisations/driver-and-vehicle-standards-agency",
      description: "Official government website for driving standards and test information"
    },
    {
      name: "Book Your Driving Test",
      url: "https://www.gov.uk/book-driving-test",
      description: "Book your practical driving test online"
    },
    {
      name: "Highway Code",
      url: "https://www.gov.uk/guidance/the-highway-code",
      description: "Essential rules and guidance for UK road users"
    },
    {
      name: "Theory Test Practice",
      url: "https://www.gov.uk/take-practice-theory-test",
      description: "Practice for your theory test"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      {/* Header */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Quick Links & Resources</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Connect with us and access helpful driving resources
          </p>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-slate-900 text-center">Follow Us</h2>
            <p className="text-center text-slate-600 mb-8 text-lg">
              Plenty of photos and videos of our vehicles, lessons, and success stories across our platforms
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {socialLinks.map((link, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 ${link.color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                        {link.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-2 text-slate-900">{link.name}</h3>
                        <p className="text-slate-600 mb-4">{link.description}</p>
                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="hover:bg-slate-100">
                            Visit <ExternalLink className="ml-2 h-4 w-4" />
                          </Button>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Separator className="max-w-5xl mx-auto" />

      {/* Useful Links Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-slate-900 text-center">Useful Driving Resources</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {usefulLinks.map((link, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-2 text-slate-900">{link.name}</h3>
                    <p className="text-slate-600 mb-4 text-sm">{link.description}</p>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="hover:bg-slate-100">
                        Visit Site <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Separator className="max-w-5xl mx-auto" />

      {/* Terms & Conditions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-none shadow-lg">
              <CardHeader className="bg-slate-50">
                <CardTitle className="text-2xl">Terms & Conditions</CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <div className="prose prose-slate max-w-none">
                  <h3 className="text-lg font-semibold mb-3">Lesson Bookings</h3>
                  <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
                    <li>Lessons must be booked in advance via phone or email</li>
                    <li>Payment is due at the time of the lesson unless otherwise agreed</li>
                    <li>We reserve the right to refuse service at our discretion</li>
                  </ul>

                  <h3 className="text-lg font-semibold mb-3">Cancellation Policy</h3>
                  <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
                    <li>Minimum 48 hours notice required for cancellations</li>
                    <li>Late cancellations (less than 48 hours) may be charged at full lesson rate</li>
                    <li>We will provide as much notice as possible if we need to cancel</li>
                  </ul>

                  <h3 className="text-lg font-semibold mb-3">Insurance & Vehicle</h3>
                  <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
                    <li>All lessons are conducted in a fully insured, dual-controlled vehicle</li>
                    <li>Students must hold a valid provisional driving licence</li>
                    <li>Students must declare any medical conditions that may affect driving</li>
                  </ul>

                  <h3 className="text-lg font-semibold mb-3">Conduct</h3>
                  <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
                    <li>Professional conduct is expected from both instructor and student</li>
                    <li>Any inappropriate behavior will result in immediate lesson termination</li>
                    <li>Punctuality is appreciated - late arrivals may result in shortened lessons</li>
                  </ul>

                  <h3 className="text-lg font-semibold mb-3">Liability</h3>
                  <p className="text-slate-700 mb-4">
                    While we take every precaution to ensure safety, we cannot be held liable for any injuries or accidents that occur during lessons beyond our insurance coverage.
                  </p>

                  <p className="text-sm text-slate-600 italic">
                    Last updated: December 2024
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Have Questions?</h2>
          <p className="text-lg mb-6">Call us now to book your lessons</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:07834618908">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-slate-100 px-10 py-7 text-2xl font-bold">
                <Phone className="mr-3 h-6 w-6" />
                07834 618908
              </Button>
            </a>
            <a href="mailto:zubairapabhai@gmail.com">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
